// src/components/CuratedFikaAdmin.jsx
// Curated Fika Admin Dashboard - Phase 1: Core Interface with Venue Selector
// Comprehensive management interface for curated fika applications and sessions

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import curatedFikaAdminService from '../services/curatedFikaAdminService';
import {
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  CogIcon,
  EnvelopeIcon,
  CalendarIcon,
  EyeIcon,
  PencilIcon,
  SparklesIcon,
  StarIcon,
  BoltIcon,
  HeartIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  PhotoIcon,
  TrashIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';

// NEW: Quick Venue Add Form Component
const QuickVenueAddForm = ({ city, onVenueAdded, onCancel }) => {
  const [venueData, setVenueData] = useState({
    name: '',
    venue_type: 'cafe',
    address: '',
    venue_area: 'Shibuya & Harajuku',
    capacity_small: 4,
    description: '',
    phone: '',
    average_cost_per_person: 15.00,
    noise_level: 'moderate'
  });
  const [loading, setLoading] = useState(false);



  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!venueData.name || !venueData.address) {
      alert('Please fill in venue name and address');
      return;
    }

    setLoading(true);
    try {
      const result = await curatedFikaAdminService.addVenue({
        ...venueData,
        city,
        country: 'Japan'
      });

      if (result.success) {
        onVenueAdded(result.data);
        alert('Venue added successfully!');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      alert(`Error adding venue: ${error.message}`);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-gray-900">Add New Venue</h4>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircleIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Venue Name *
          </label>
          <input
            type="text"
            value={venueData.name}
            onChange={(e) => setVenueData({...venueData, name: e.target.value})}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="e.g., Blue Bottle Coffee Shibuya"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Venue Type
          </label>
          <select
            value={venueData.venue_type}
            onChange={(e) => setVenueData({...venueData, venue_type: e.target.value})}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="cafe">Cafe</option>
            <option value="restaurant">Restaurant</option>
            <option value="bar">Bar</option>
            <option value="hotel_lobby">Hotel Lobby</option>
            <option value="coworking">Coworking Space</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address *
        </label>
        <input
          type="text"
          value={venueData.address}
          onChange={(e) => setVenueData({...venueData, address: e.target.value})}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
          placeholder="Full address including building name"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Area
          </label>
          <select
            value={venueData.venue_area}
            onChange={(e) => setVenueData({...venueData, venue_area: e.target.value})}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="Shibuya & Harajuku">Shibuya & Harajuku</option>
            <option value="Ginza & Marunouchi">Ginza & Marunouchi</option>
            <option value="Omotesando & Aoyama">Omotesando & Aoyama</option>
            <option value="Shinjuku">Shinjuku</option>
            <option value="Roppongi">Roppongi</option>
            <option value="Nakameguro">Nakameguro</option>
            <option value="Setagaya City">Setagaya City</option>
            <option value="Minato">Minato</option>
            <option value="Koto City">Koto City</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Capacity (2-4 people)
          </label>
          <input
            type="number"
            value={venueData.capacity_small}
            onChange={(e) => setVenueData({...venueData, capacity_small: parseInt(e.target.value)})}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
            min="2"
            max="10"
          />
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
        >
          {loading ? 'Adding...' : 'Add Venue'}
        </button>
      </div>
    </form>
  );
};

// NEW: Venue Selector Component
const VenueSelector = ({ selectedVenue, onVenueSelect, city = 'Tokyo' }) => {
  const [venues, setVenues] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    venue_type: '',
    venue_area: ''
  });
  
  useEffect(() => {
    loadVenues();
  }, [city, filters]);

  const loadVenues = async () => {
    setLoading(true);
    try {
      const result = await curatedFikaAdminService.getVenues({ 
        city, 
        ...filters 
      });
      if (result.success) {
        setVenues(result.data);
      }
    } catch (error) {
      console.error('Error loading venues:', error);
    }
    setLoading(false);
  };

  const handleVenueSelect = (venue) => {
    onVenueSelect({
      venue_name: venue.name,
      venue_address: venue.address,
      venue_area: venue.venue_area,
      venue_id: venue.id,
      manual_entry: false
    });
  };

  const handleVenueAdded = (newVenue) => {
    loadVenues();
    setShowAddForm(false);
    handleVenueSelect(newVenue);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Select Venue ({city})
        </label>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="text-sm text-purple-600 hover:text-purple-800 flex items-center"
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          Add New Venue
        </button>
      </div>

      {/* Venue Filters */}
      <div className="grid grid-cols-2 gap-3">
        <select
          value={filters.venue_type}
          onChange={(e) => setFilters({...filters, venue_type: e.target.value})}
          className="text-sm border border-gray-300 rounded-md px-2 py-1"
        >
          <option value="">All Types</option>
          <option value="cafe">Cafes</option>
          <option value="restaurant">Restaurants</option>
          <option value="bar">Bars</option>
          <option value="hotel_lobby">Hotel Lobbies</option>
        </select>
        
        <select
          value={filters.venue_area}
          onChange={(e) => setFilters({...filters, venue_area: e.target.value})}
          className="text-sm border border-gray-300 rounded-md px-2 py-1"
        >
          <option value="">All Areas</option>
          <option value="Shibuya & Harajuku">Shibuya & Harajuku</option>
          <option value="Ginza & Marunouchi">Ginza & Marunouchi</option>
          <option value="Omotesando & Aoyama">Omotesando & Aoyama</option>
          <option value="Shinjuku">Shinjuku</option>
          <option value="Roppongi">Roppongi</option>
          <option value="Setagaya City">Setagaya City</option>
          <option value="Nakameguro">Nakameguro</option>
          <option value="Setagaya City">Kitazawa</option>
          <option value="Koto City">Koto City</option>
        </select>
      </div>

      {/* Add Venue Form */}
      {showAddForm && (
        <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
          <QuickVenueAddForm 
            city={city}
            onVenueAdded={handleVenueAdded}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}

      {/* Venue Selection Grid */}
      {loading ? (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
          <p className="text-sm text-gray-600 mt-2">Loading venues...</p>
        </div>
      ) : venues.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <BuildingOfficeIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
          <p className="text-sm">No venues found for {city}</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="text-purple-600 hover:text-purple-800 text-sm mt-2"
          >
            Add the first venue
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
          {venues.map(venue => (
            <div
              key={venue.id}
              onClick={() => handleVenueSelect(venue)}
              className={`border rounded-lg p-4 cursor-pointer transition-all hover:border-purple-500 ${
                selectedVenue?.venue_id === venue.id 
                  ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' 
                  : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h4 className="font-medium text-gray-900">{venue.name}</h4>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                      {venue.venue_type}
                    </span>
                    {venue.rating && (
                      <span className="text-yellow-500 text-sm">
                        ⭐ {venue.rating}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{venue.address}</p>
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>📍 {venue.venue_area}</span>
                    {venue.capacity_small && <span>👥 {venue.capacity_small} seats</span>}
                    {venue.noise_level && <span>🔊 {venue.noise_level}</span>}
                    {venue.usage_count > 0 && <span>Used {venue.usage_count}x</span>}
                  </div>
                </div>
                {venue.image_url ? (
                  <img 
                    src={venue.image_url} 
                    alt={venue.name}
                    className="w-16 h-16 rounded-lg object-cover ml-4"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center ml-4">
                    <PhotoIcon className="w-6 h-6 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Fallback to Manual Entry */}
      <div className="border-t pt-4">
        <button
          onClick={() => onVenueSelect({ 
            venue_name: '', 
            venue_address: '', 
            venue_area: 'Shibuya & Harajuku',
            manual_entry: true 
          })}
          className="text-sm text-gray-600 hover:text-gray-800 flex items-center"
        >
          <PencilIcon className="w-4 h-4 mr-1" />
          Enter venue details manually instead
        </button>
      </div>
    </div>
  );
};


const CuratedFikaAdmin = () => {
  // State management
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  const [selectedSession, setSelectedSession] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [sessionDetailModal, setSessionDetailModal] = useState(false);
  const [editSessionModal, setEditSessionModal] = useState(false);

  // 9-22-25
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [availableApplications, setAvailableApplications] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState('');
 
  const [matchingFilters, setMatchingFilters] = useState({
    city: 'Tokyo',
    country: 'Japan'
  });

  // 11-16-25
  const [analyticsData, setAnalyticsData] = useState({
    overview: null,
    revenue: null,
    sessions: null,
    applications: null,
    feedback: null,
    trends: null
  });

  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDateRange, setAnalyticsDateRange] = useState({
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Last 90 days
    endDate: new Date().toISOString().split('T')[0]
  });
  //
  

  
  // Filters and pagination
  const [filters, setFilters] = useState({
    status: '',
    goal_category: '',
    email: '',
    date_from: '',
    date_to: '',
    location_preference: '',
    career_stage: ''
  });
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });


  // 9/17/25
  // Add to existing state declarations
  const [feedback, setFeedback] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [feedbackPagination, setFeedbackPagination] = useState({ total: 0, limit: 20, offset: 0 });
  const [feedbackFilters, setFeedbackFilters] = useState({
    session_id: '',
    min_satisfaction: '',
    venue_area: '',
    date_from: '',
    date_to: '',
    connection_made: '',
    would_attend_again: ''
  });

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // 10-15-25
  // Add to component state declarations
const [showOpenSessionModal, setShowOpenSessionModal] = useState(false);
const [openSessionData, setOpenSessionData] = useState({
  session_title: '',
  session_date: '',
  venue_name: '',
  venue_address: '',
  venue_area: 'Shibuya & Harajuku',
  city: 'Tokyo',
  country: 'Japan',
  max_participants: 4,
  session_duration: 90,
  price_per_person: 30.00,
  host_notes: '',
  target_interests: [],
  target_themes: []
});

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadStats(),
        loadApplications(),
        loadSessions(),
        loadFeedback(),  // 9-17-25
        loadFeedbackStats()  // 9-17-25
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      alert('Error loading admin data');
    }
    setLoading(false);
  };

  // API helper function using the service
  const callAdminAPI = async (action, data = null) => {
    try {
      let result;
      
      switch (action) {
        case 'get_admin_stats':
          result = await curatedFikaAdminService.getAdminStats();
          break;
        case 'get_applications':
          result = await curatedFikaAdminService.getApplications(filters, pagination);
          break;
        case 'get_sessions':
          result = await curatedFikaAdminService.getSessions(filters);
          break;
        case 'update_application_status':
          result = await curatedFikaAdminService.updateApplicationStatus(
            data.applicationId, 
            data.status, 
            data.notes
          );
          break;
        case 'run_ai_matching':
          result = await curatedFikaAdminService.runAIMatching(data);
          break;
        case 'create_session_from_applications':
          result = await curatedFikaAdminService.createSessionFromApplications(
            data.applicationIds, 
            data.sessionData
          );
          break;
        case 'send_session_invitations':
          result = await curatedFikaAdminService.sendSessionInvitations(data.sessionId);
          break;
        case 'send_feedback_emails':
          result = await curatedFikaAdminService.sendFeedbackEmails(data.sessionId);
          break;
        // NEW VENUE CASES:
        case 'get_venues':
          result = await curatedFikaAdminService.getVenues(data);
          break;
        case 'add_venue':
          result = await curatedFikaAdminService.addVenue(data);
          break;
        // 9-17-25
        case 'get_feedback':
          result = await curatedFikaAdminService.getFeedback(data, filters);
          break;
        case 'get_feedback_stats':
          result = await curatedFikaAdminService.getFeedbackStats();
          break;
        // NEW SESSION CASES:
        case 'update_session_details':
          result = await curatedFikaAdminService.updateSessionDetails(
            data.sessionId,
            data.updates
          );
          break;
        case 'update_session_status':
          result = await curatedFikaAdminService.updateSessionStatus(
            data.sessionId,
            data.status
          );
          break;
        // 9-25-25
        case 'send_confirmation_nudges':
          result = await sendConfirmationNudges(supabaseClient, data?.sessionId);
          break;

        default:
          throw new Error(`Unknown action: ${action}`);
      }
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      return result;
    } catch (error) {
      console.error(`Error in ${action}:`, error);
      throw error;
    }
  };

  // Data loading functions
  const loadStats = async () => {
    try {
      const result = await callAdminAPI('get_admin_stats');
      setStats(result.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // 9-26-25 latest
  // Add this new function after the existing loadApplications function
const loadApplicationsWithPagination = async (newPagination = null) => {
  try {
    const paginationToUse = newPagination || pagination;
    console.log('Loading applications with pagination:', paginationToUse);
    
    const result = await curatedFikaAdminService.getApplications(filters, paginationToUse);
    
    if (result.success && result.data) {
      const applicationsData = result.data.data;
      setApplications(Array.isArray(applicationsData) ? applicationsData : []);
      
      // Update pagination state if new pagination was provided
      if (newPagination) {
        setPagination(prev => ({ 
          ...prev, 
          offset: newPagination.offset,
          total: result.data.pagination?.total || 0 
        }));
      } else {
        setPagination(prev => ({ ...prev, total: result.data.pagination?.total || 0 }));
      }
    } else {
      setApplications([]);
    }
  } catch (error) {
    console.error('Error loading applications with pagination:', error);
    setApplications([]);
  }
};

// Update the existing applyFilters function to use the new pagination loader
const applyFilters = () => {
  // Reset to first page when applying filters
  const resetPagination = { ...pagination, offset: 0 };
  loadApplicationsWithPagination(resetPagination);
};


  const loadApplications = async () => {
    try {
      const result = await curatedFikaAdminService.getApplications(filters, pagination);
      
      console.log('🔍 API Response Structure:', result);
      
      if (result.success && result.data) {
        const applicationsData = result.data.data;
        setApplications(Array.isArray(applicationsData) ? applicationsData : []);
        setPagination(prev => ({ ...prev, total: result.data.pagination?.total || 0 }));
      } else {
        setApplications([]);
      }
    } catch (error) {
      console.error('Error loading applications:', error);
      setApplications([]);
    }
  };

  // 10-5-25
  const applySessionFilters = () => {
    loadSessions(filters);
  };

  const clearSessionFilters = () => {
    setFilters({
      ...filters,
      session_status: '',
      venue_area: '',
      date_from: '',
      date_to: ''
    });
    setTimeout(() => loadSessions(), 100);
  };

  const loadSessions = async (sessionFilters = null) => {
    try {
      const filtersToUse = sessionFilters || filters;
      const result = await callAdminAPI('get_sessions', null, filtersToUse);
      setSessions(result.data || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const origloadSessions = async () => {
    try {
      const result = await callAdminAPI('get_sessions');
      setSessions(result.data || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  // 9/17/25
  // Add these functions after the existing loadSessions function
const loadFeedback = async () => {
  try {
    const result = await curatedFikaAdminService.getFeedback(feedbackFilters, feedbackPagination);
    
    if (result.success && result.data) {
      const feedbackData = result.data.data;
      setFeedback(Array.isArray(feedbackData) ? feedbackData : []);
      setFeedbackPagination(prev => ({ ...prev, total: result.data.pagination?.total || 0 }));
    } else {
      setFeedback([]);
    }
  } catch (error) {
    console.error('Error loading feedback:', error);
    setFeedback([]);
  }
};

const loadFeedbackStats = async () => {
  try {
    const result = await curatedFikaAdminService.getFeedbackStats();
    if (result.success) {
      setFeedbackStats(result.data);
    }
  } catch (error) {
    console.error('Error loading feedback stats:', error);
  }
};

const handleFeedbackFilterChange = (key, value) => {
  setFeedbackFilters(prev => ({ ...prev, [key]: value }));
  setFeedbackPagination(prev => ({ ...prev, offset: 0 }));
};

// 10-15-25
const handleCreateOpenSession = async () => {
  // Validation
  if (!openSessionData.session_title || !openSessionData.session_date || 
      !openSessionData.venue_name) {
    alert('Please fill in all required fields (title, date, venue)');
    return;
  }

  if (openSessionData.max_participants < 2 || openSessionData.max_participants > 4) {
    alert('Max participants must be between 2 and 4');
    return;
  }

  if (!confirm('Create open registration session? Members will be able to self-register.')) {
    return;
  }

  setLoading(true);
  try {
    const result = await curatedFikaAdminService.createOpenSession(openSessionData);

    if (result.success) {
      alert(`✅ Open session created successfully!\n\nSession: "${result.sessionTitle}"\nMax participants: ${result.maxParticipants}\n\nMembers can now register from their portal.`);
      
      // Reset form and close modal
      setOpenSessionData({
        session_title: '',
        session_date: '',
        venue_name: '',
        venue_address: '',
        venue_area: 'Shibuya & Harajuku',
        city: 'Tokyo',
        country: 'Japan',
        max_participants: 4,
        session_duration: 90,
        price_per_person: 30.00,
        host_notes: '',
        target_interests: [],
        target_themes: []
      });
      setShowOpenSessionModal(false);
      
      // Refresh sessions list
      loadSessions();
      loadStats();
    } else {
      alert(`❌ Error: ${result.error}`);
    }
  } catch (error) {
    alert(`❌ Error creating open session: ${error.message}`);
  }
  setLoading(false);
};

const applyFeedbackFilters = () => {
  loadFeedback();
};

const clearFeedbackFilters = () => {
  setFeedbackFilters({
    session_id: '',
    min_satisfaction: '',
    venue_area: '',
    date_from: '',
    date_to: '',
    connection_made: '',
    would_attend_again: ''
  });
  setFeedbackPagination(prev => ({ ...prev, offset: 0 }));
  setTimeout(loadFeedback, 100);
};

const getRatingColor = (rating) => {
  if (rating >= 8) return 'text-green-600';
  if (rating >= 6) return 'text-yellow-600';
  if (rating >= 4) return 'text-orange-600';
  return 'text-red-600';
};

const getRatingStars = (rating) => {
  return '★'.repeat(Math.floor(rating)) + '☆'.repeat(10 - Math.floor(rating));
};




// Add this helper function at the top of the CuratedFikaAdmin component
// 9-20-25
const handleDeleteSession = async (session) => {
  const participantCount = session.curated_fika_participants?.length || 0;
  const confirmedCount = session.curated_fika_participants?.filter(p => p.participation_status === 'confirmed').length || 0;
  const paidCount = session.curated_fika_participants?.filter(p => p.payment_status === 'paid').length || 0;

  // Build warning message
  let warningMessage = `⚠️ DELETE SESSION CONFIRMATION\n\n`;
  warningMessage += `Session: "${session.session_title}"\n`;
  warningMessage += `Date: ${formatDate(session.session_date)}\n`;
  warningMessage += `Status: ${session.session_status}\n`;
  warningMessage += `Participants: ${participantCount}\n`;
  
  if (confirmedCount > 0) {
    warningMessage += `⚠️ Confirmed Participants: ${confirmedCount}\n`;
  }
  
  if (paidCount > 0) {
    warningMessage += `💰 Paid Participants: ${paidCount}\n`;
  }
  
  warningMessage += `\n🗑️ This action CANNOT be undone.\n`;
  warningMessage += `All participant data, feedback, and matches will be permanently deleted.\n\n`;
  
  if (paidCount > 0) {
    warningMessage += `❌ Cannot delete session with paid participants.\n`;
    warningMessage += `Please process refunds first or use force delete.`;
    alert(warningMessage);
    return;
  }
  
  warningMessage += `Are you sure you want to delete this session?`;

  if (!confirm(warningMessage)) {
    return;
  }

  // For sessions with confirmed participants, ask about cancellation emails
  let sendCancellationEmails = false;
  if (confirmedCount > 0) {
    sendCancellationEmails = confirm(
      `Send cancellation emails to ${confirmedCount} confirmed participants?`
    );
  }

  setLoading(true);
  try {
    const result = await curatedFikaAdminService.deleteSessionWithValidation(
      session.id,
      false, // forceDelete
      confirmedCount > 0, // acknowledgeConfirmations
      sendCancellationEmails
    );

    if (result.success) {
      alert(`✅ Session deleted successfully!\n\nParticipants affected: ${result.participants_affected || 0}`);
      loadSessions(); // Refresh the sessions list
      loadStats(); // Refresh stats
    } else {
      if (result.validation_failed) {
        // Show detailed validation error with option to force delete
        const forceDeleteMessage = `${result.error}\n\nWould you like to force delete anyway?`;
        if (confirm(forceDeleteMessage)) {
          await handleForceDeleteSession(session.id, sendCancellationEmails);
        }
      } else {
        alert(`❌ Error: ${result.error}`);
      }
    }
  } catch (error) {
    alert(`❌ Error: ${error.message}`);
  }
  setLoading(false);
};

// 9-20-25
const handleForceDeleteSession = async (sessionId, sendCancellationEmails) => {
  const result = await curatedFikaAdminService.deleteSessionWithValidation(
    sessionId,
    true, // forceDelete = true
    true, // acknowledgeConfirmations = true
    sendCancellationEmails
  );

  if (result.success) {
    alert(`✅ Session force deleted!\n\nParticipants affected: ${result.participants_affected || 0}`);
    loadSessions();
    loadStats();
  } else {
    alert(`❌ Force delete failed: ${result.error}`);
  }
};

// 9-22-25
// Add this function alongside your other handlers
const handleDeleteParticipant = async (participant, session) => {
  const member = participant.curated_fika_applications;
  const participantName = `${member.first_name} ${member.last_name}`;
  
  // Build confirmation message
  let confirmMessage = `⚠️ REMOVE PARTICIPANT\n\n`;
  confirmMessage += `Participant: ${participantName}\n`;
  confirmMessage += `Email: ${member.email}\n`;
  confirmMessage += `Session: "${session.session_title}"\n`;
  confirmMessage += `Status: ${participant.participation_status}\n`;
  confirmMessage += `Payment: ${participant.payment_status}\n\n`;
  
  // Warning about paid participants
  if (participant.payment_status === 'paid') {
    confirmMessage += `💰 This participant has already paid.\n`;
    confirmMessage += `Consider processing a refund before removal.\n\n`;
  }
  
  confirmMessage += `This action will:\n`;
  confirmMessage += `• Remove the participant from the session\n`;
  confirmMessage += `• Delete their feedback if any exists\n`;
  confirmMessage += `• Update the session participant count\n\n`;
  confirmMessage += `This action CANNOT be undone.\n\n`;
  confirmMessage += `Are you sure you want to remove ${participantName}?`;

  if (!confirm(confirmMessage)) {
    return;
  }

  setLoading(true);
  try {
    const result = await curatedFikaAdminService.deleteParticipant(
      participant.id,
      {
        send_removal_email: false // Could add checkbox for this
      }
    );

    if (result.success) {
      alert(`✅ Participant removed successfully!\n\n${result.message}`);
      
      // Refresh session details
      if (selectedSession) {
        const updatedSession = await curatedFikaAdminService.getSessionDetails(selectedSession.id);
        if (updatedSession.success) {
          setSelectedSession(updatedSession.data);
        }
      }
      
      // Refresh sessions list and stats
      loadSessions();
      loadStats();
    } else {
      alert(`❌ Error: ${result.error}`);
    }
  } catch (error) {
    alert(`❌ Error: ${error.message}`);
  }
  setLoading(false);
};

// 9-22-25
// Add participant handler
const handleAddParticipant = async () => {
  if (!selectedApplicationId) {
    alert('Please select an application');
    return;
  }



  const confirmMessage = `Add participant to "${selectedSession.session_title}"?`;
  if (!confirm(confirmMessage)) return;

  setLoading(true);
  try {
    const result = await curatedFikaAdminService.addParticipantToSession(
      selectedSession.id,
      selectedApplicationId,
      {
        send_invitation_email: true,
        is_replacement: false
      }
    );

    if (result.success) {
      alert(`✅ Participant added successfully!\n\n${result.message}`);
      
      // Refresh session details
      const updatedSession = await curatedFikaAdminService.getSessionDetails(selectedSession.id);
      if (updatedSession.success) {
        setSelectedSession(updatedSession.data);
      }
      
      setShowAddParticipant(false);
      setSelectedApplicationId('');
      loadSessions();
      loadStats();
    } else {
      alert(`❌ Error: ${result.error}`);
    }
  } catch (error) {
    alert(`❌ Error: ${error.message}`);
  }
  setLoading(false);
};

// Load available applications
const loadAvailableApplications = async () => {
  try {
    const result = await curatedFikaAdminService.getApplications({
      status: 'approved'
    }, { limit: 100 });
    
    if (result.success) {
      // Filter out applications already in this session
      const currentParticipantIds = selectedSession.curated_fika_participants?.map(p => p.application_id) || [];
      const available = result.data.data.filter(app => 
        !currentParticipantIds.includes(app.id) && app.is_premium_member
      );
      setAvailableApplications(available);
    }
  } catch (error) {
    console.error('Error loading available applications:', error);
  }
};

  // Action handlers
  const getSessionStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
      confirmed: 'bg-green-100 text-green-800 border-green-200',
      in_progress: 'bg-orange-100 text-orange-800 border-orange-200',
      completed: 'bg-purple-100 text-purple-800 border-purple-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };
  
  const getParticipantStatusColor = (status) => {
    const colors = {
      invited: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      attended: 'bg-purple-100 text-purple-800',
      no_show: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };
  
  const oldupdateSessionDetails = async (sessionId, updates) => {
    if (!confirm('Update session details?')) return;
  
    setLoading(true);
    try {
      console.log('🔍 FRONTEND DEBUG - Raw updates object:', updates);
      console.log('🔍 FRONTEND DEBUG - Session date value:', updates.session_date);
      console.log('🔍 FRONTEND DEBUG - Type of session_date:', typeof updates.session_date);

      await callAdminAPI('update_session_details', {
        sessionId,
        updates
      });
      
      alert('Session updated successfully!');
      setEditSessionModal(false);
      setEditingSession(null);
      loadSessions();
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setLoading(false);
  };

  const origupdateSessionDetails = async (sessionId, updates) => {
    if (!confirm('Update session details?')) return;
  
    setLoading(true);
    try {
      const updatesToSend = { ...updates };
      
      // ✅ Use session_date_local if available, otherwise use session_date
      if (updates.session_date_local || updates.session_date) {
        const timezone = updates.venue_timezone || 'Asia/Tokyo';
        const localDateString = updates.session_date_local || updates.session_date;
        updatesToSend.session_date = convertLocalToUTC(localDateString, timezone);
        
        // Clean up the temporary local field
        delete updatesToSend.session_date_local;
        
        console.log('🔍 Date Conversion Debug:');
        console.log('  Local time entered:', localDateString);
        console.log('  Timezone:', timezone);
        console.log('  UTC time to save:', updatesToSend.session_date);
      }
  
      await callAdminAPI('update_session_details', {
        sessionId,
        updates: updatesToSend
      });
      
      alert('Session updated successfully!');
      setEditSessionModal(false);
      setEditingSession(null);
      loadSessions();
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setLoading(false);
  };


  const updateSessionDetails = async (sessionId, updates) => {
    if (!confirm('Update session details?')) return;
  
    setLoading(true);
    try {
      const updatesToSend = { ...updates };
      
      // ✅ Send the local datetime string directly - let backend convert it
      if (updates.session_date_local) {
        updatesToSend.session_date = updates.session_date_local;
        delete updatesToSend.session_date_local;
        
        console.log('🔍 Sending to backend:');
        console.log('  Local time (Tokyo):', updatesToSend.session_date);
        console.log('  Backend will convert to UTC');
      }
  
      await callAdminAPI('update_session_details', {
        sessionId,
        updates: updatesToSend
      });
      
      alert('Session updated successfully!');
      setEditSessionModal(false);
      setEditingSession(null);
      loadSessions();
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setLoading(false);
  };

  const convertLocalToUTC = (localDateString, timezone) => {
    if (!localDateString) return null;
    
    // Timezone offset hours from UTC
    const timezoneOffsets = {
      'Asia/Tokyo': 9,
      'Asia/Osaka': 9
    };
    
    const offsetHours = timezoneOffsets[timezone] || 9;
    
    // Parse the datetime string components
    const [datePart, timePart] = localDateString.split('T');
    const [year, month, day] = datePart.split('-');
    const [hour, minute] = timePart.split(':');
    
    // Create a UTC date by treating the input as Tokyo time and subtracting offset
    // If Tokyo is 11:00 AM (UTC+9), UTC should be 2:00 AM (11 - 9 = 2)
    const tokyoHour = parseInt(hour);
    const utcHour = tokyoHour - offsetHours;
    
    // Create UTC date string
    const utcDate = new Date(Date.UTC(
      parseInt(year),
      parseInt(month) - 1, // Month is 0-indexed
      parseInt(day),
      utcHour,
      parseInt(minute),
      0
    ));
    
    return utcDate.toISOString();
  };
  
  const updateSessionStatus = async (sessionId, newStatus) => {
    if (!confirm(`Update session status to "${newStatus}"?`)) return;
  
    setLoading(true);
    try {
      await callAdminAPI('update_session_status', {
        sessionId,
        status: newStatus
      });
      
      alert('Session status updated successfully!');
      loadSessions();
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setLoading(false);
  };

  const handleSendFeedbackEmails = async (sessionId) => {
     // ✅ ADD: Validate parameters before proceeding
    if (!sessionId) {
      alert('Error: No session ID provided');
      return;
    }

    // ✅ IMPROVED: More specific confirmation message
    if (!confirm(`Send feedback emails for:\n\n"${sessionTitle}"\n\nSession ID: ${sessionId}\n\nThis will email all attended participants of THIS SESSION ONLY.`)) {
      return;
    }
      
   
  
    setLoading(true);
    try {
      const result = await callAdminAPI('send_feedback_emails', { sessionId });
      
      if (result.success) {
        const verificationMessage = result.sessionTitle 
        ? `✅ Feedback emails sent successfully!\n\nSession: "${result.sessionTitle}"\nSession ID: ${result.sessionId}\n\nEmails sent: ${result.emailsSent}/${result.totalParticipants}\n\nParticipants can now provide their feedback.`
        : `Feedback emails sent successfully!\n\nSent: ${result.emailsSent}/${result.totalParticipants} emails\n\nParticipants can now provide their feedback.`;
      
        alert(`Feedback emails sent successfully!\n\nSent: ${result.emailsSent}/${result.totalParticipants} emails\n\nParticipants can now provide their feedback.`);
         // ✅ ADD: Log for verification
        console.log(`✅ Feedback emails sent for session ${sessionId}:`, result);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      alert(`❌ Error sending feedback emails for session "${sessionTitle}":\n\n${error.message}`);
      console.error(`❌ Frontend error for session ${sessionId}:`, error);
      alert(`Error: ${error.message}`);
    }
    setLoading(false);
  };

  const updateApplicationStatus = async (applicationId, newStatus, notes = '') => {
    if (!confirm(`Update application status to "${newStatus}"?`)) return;

    setLoading(true);
    try {
      await callAdminAPI('update_application_status', {
        applicationId,
        status: newStatus,
        notes
      });
      
      alert('Application status updated successfully!');
      loadApplications();
      loadStats();
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setLoading(false);
  };

  const runAIMatching = async () => {
    if (!matchingFilters.city || !matchingFilters.country) {
      alert('Please select both city and country before running AI matching.');
      return;
    }
  
    if (!confirm(`Run AI matching algorithm for ${matchingFilters.city}, ${matchingFilters.country}? This will create optimized curated sessions.`)) return;
  
    setLoading(true);
    try {
      const result = await callAdminAPI('run_ai_matching', {
        city: matchingFilters.city,
        country: matchingFilters.country
      });
      
      if (result.sessionsCreated > 0) {
        alert(`AI matching completed for ${matchingFilters.city}, ${matchingFilters.country}!\n\n${result.sessionsCreated} new sessions created\nProcessed ${result.applicationsProcessed} applications`);
        loadApplications();
        loadSessions();
        loadStats();
      } else {
        alert(result.message || `No sessions were created for ${matchingFilters.city}, ${matchingFilters.country}. Need more compatible applications.`);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setLoading(false);
  };

  const createManualSession = async () => {
    if (selectedItems.length < 2 || selectedItems.length > 4) {
      alert('Please select 2-4 applications to create a curated session.');
      return;
    }

    const sessionTitle = prompt('Enter session title (optional):');
    const venueNotes = prompt('Enter venue preferences (optional):');
    
    setLoading(true);
    try {
      const firstApp = applications.find(app => selectedItems.includes(app.id));
      
      const sessionData = {
        session_title: sessionTitle || `Curated Fika - ${firstApp.location_preference}`,
        venue_notes: venueNotes || 'Premium venue in preferred area',
        max_participants: selectedItems.length,
        price_per_person: 30.00,
        facilitator_notes: `Manually created session on ${new Date().toISOString()}`
      };

      const result = await callAdminAPI('create_session_from_applications', {
        applicationIds: selectedItems,
        sessionData
      });

      alert(`Session created successfully!\nSession ID: ${result.sessionId}\nParticipants: ${result.participantCount}`);
      
      setSelectedItems([]);
      loadApplications();
      loadSessions();
      loadStats();
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setLoading(false);
  };

  const sendSessionInvitations = async (sessionId, sessionTitle) => {
    if (!confirm(`Send invitations to all participants in "${sessionTitle}"?`)) return;

    setLoading(true);
    try {
      const result = await callAdminAPI('send_session_invitations', { sessionId });
      
      alert(`Session invitations sent!\n\nSent: ${result.invitationsSent}/${result.totalParticipants} invitations\n\nParticipants can now confirm their attendance.`);
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setLoading(false);
  };

  // Utility functions
  const getStatusColor = (status) => {
    return curatedFikaAdminService.getStatusColor(status);
  };

  const formatDate = (dateString) => {
    return curatedFikaAdminService.formatDate(dateString);
  };

  const formatDateTime = (dateString) => {
    return curatedFikaAdminService.formatDateTime(dateString);
  };

  // 11-15-25
  // Add these RIGHT AFTER the formatDateTime function (around line 547)

// ✅ Helper function to get subscription display
const getSubscriptionDisplay = (app) => {
  if (!app.subscription_plan_name) {
    return {
      label: 'Not Subscribed',
      color: 'bg-gray-100 text-gray-600',
      icon: '⚫'
    };
  }

  // Map billing intervals to display names
  const planDisplay = {
    'month': 'Monthly',
    '6_months': '6-Month',
    'year': 'Yearly'
  };

  const interval = app.subscription_billing_interval || 'month';
  const displayName = planDisplay[interval] || 'Premium';

  // Different colors for different plan types
  const colors = {
    'month': 'bg-blue-100 text-blue-700',
    '6_months': 'bg-purple-100 text-purple-700',
    'year': 'bg-green-100 text-green-700'
  };

  const icons = {
    'month': '🔵',
    '6_months': '🟣',
    'year': '🟢'
  };

  return {
    label: app.is_trial ? `${displayName} (Trial)` : displayName,
    color: colors[interval] || 'bg-blue-100 text-blue-700',
    icon: icons[interval] || '💎'
  };
};

// ✅ Helper function to format subscription expiry
const formatSubscriptionExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  
  const expiry = new Date(expiryDate);
  const now = new Date();
  const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  
  const formatted = formatDate(expiryDate);
  
  if (daysUntilExpiry < 0) {
    return { text: `Expired ${formatted}`, color: 'text-red-600' };
  } else if (daysUntilExpiry <= 7) {
    return { text: `${formatted} (${daysUntilExpiry}d)`, color: 'text-orange-600' };
  } else if (daysUntilExpiry <= 30) {
    return { text: `${formatted} (${daysUntilExpiry}d)`, color: 'text-yellow-600' };
  } else {
    return { text: formatted, color: 'text-gray-600' };
  }
};

  // Filter handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const origapplyFilters = () => {
    loadApplications();
  };

  const convertUTCToLocal = (utcDateString, timezone) => {
    if (!utcDateString) return '';
    
    const utcDate = new Date(utcDateString);
    const localTime = utcDate.toLocaleString('sv-SE', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return localTime.replace(' ', 'T');
  };

  // 9-23-25
  const handleSendSessionReminder = async (sessionId, sessionTitle) => {
    if (!confirm(`Send reminder emails for "${sessionTitle}"?`)) return;
  
    setLoading(true);
    try {
      const result = await curatedFikaAdminService.sendSessionReminder(sessionId);
      
      if (result.success) {
        if (result.remindersSent > 0) {
          alert(`✅ Reminders sent successfully!\n\nSession: ${result.sessionTitle}\nReminders sent: ${result.remindersSent}/${result.totalEligible} participants\n\nParticipants will receive their reminder emails.`);
        } else {
          alert(`ℹ️ No new reminders needed.\n\nAll eligible participants have already received reminders for this session.`);
        }
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
    setLoading(false);
  };

  // 9-25-25
  const handleSendConfirmationNudges = async (sessionId, sessionTitle) => {
    if (!confirm(`Send confirmation nudges to non-responders for "${sessionTitle}"?\n\nThis will send reminder emails to participants who haven't confirmed yet.`)) return;
  
    setLoading(true);
    try {
      const result = await curatedFikaAdminService.sendConfirmationNudges(sessionId);
      
      if (result.success) {
        if (result.nudgesSent > 0) {
          alert(`✅ Confirmation nudges sent successfully!\n\nSession: ${sessionTitle}\nTotal nudges sent: ${result.nudgesSent}\n\nFirst nudges: ${result.firstNudges}\nSecond nudges: ${result.secondNudges}\n\nParticipants will receive reminder emails to confirm their attendance.`);
        } else {
          alert(`ℹ️ No nudges needed for "${sessionTitle}".\n\nAll participants have either confirmed or already received recent nudges.`);
        }
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      alert(`❌ Error sending confirmation nudges: ${error.message}`);
    }
    setLoading(false);
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      goal_category: '',
      email: '',
      date_from: '',
      date_to: '',
      location_preference: '',
      career_stage: ''
    });
    setPagination(prev => ({ ...prev, offset: 0 }));
    setTimeout(loadApplications, 100);
  };

  // Tab navigation
  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'applications', name: 'Applications', icon: UserGroupIcon },
    { id: 'sessions', name: 'Sessions', icon: SparklesIcon },
    { id: 'feedback', name: 'Feedback', icon: HeartIcon },
    { id: 'matching', name: 'AI Matching', icon: CogIcon },
    { id: 'analytics', name: 'Analytics', icon: StarIcon }
  ];

  if (loading && !stats && !applications.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading curated fika admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <SparklesIcon className="w-8 h-8 text-amber-500 mr-3" />
                Curated Fika Admin
              </h1>
              <p className="text-gray-600">Manage premium intimate Swedish coffee experiences</p>
            </div>
            <div className="flex items-center space-x-3">
              {loading && (
                <div className="flex items-center text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                  Loading...
                </div>
              )}
              <button
                onClick={loadInitialData}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center text-sm"
              >
                🔄 Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex space-x-8">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Grid */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <UserGroupIcon className="w-8 h-8 text-amber-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Applications</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.applications?.total || 0}</p>
                      <p className="text-xs text-gray-500">
                        {stats.trends?.applicationsThisWeek || 0} this week
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <ClockIcon className="w-8 h-8 text-yellow-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Pending Review</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.applications?.pending || 0}</p>
                      <p className="text-xs text-gray-500">
                        Awaiting approval
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <SparklesIcon className="w-8 h-8 text-purple-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Active Sessions</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.sessions?.active || 0}</p>
                      <p className="text-xs text-gray-500">
                        Premium experiences
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <ChartBarIcon className="w-8 h-8 text-green-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Revenue This Month</p>
                      <p className="text-2xl font-bold text-gray-900">${stats.revenue?.thisMonth || 0}</p>
                      <p className="text-xs text-gray-500">
                        Premium pricing
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={runAIMatching}
                  className="bg-purple-600 text-white p-4 rounded-lg hover:bg-purple-700 flex items-center justify-center"
                >
                  <BoltIcon className="w-5 h-5 mr-2" />
                  Run AI Matching
                </button>
                
                <button
                  onClick={() => setActiveTab('applications')}
                  className="bg-amber-600 text-white p-4 rounded-lg hover:bg-amber-700 flex items-center justify-center"
                >
                  <EyeIcon className="w-5 h-5 mr-2" />
                  Review Applications
                </button>

                <button
                  onClick={() => setActiveTab('sessions')}
                  className="bg-green-600 text-white p-4 rounded-lg hover:bg-green-700 flex items-center justify-center"
                >
                  <SparklesIcon className="w-5 h-5 mr-2" />
                  Manage Sessions
                </button>

                <button
                  onClick={() => {
                    setActiveTab('sessions');
                    setTimeout(() => setShowOpenSessionModal(true), 100);
                  }}
                  className="bg-blue-600 text-white p-4 rounded-lg hover:bg-blue-700 flex items-center justify-center"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Create Open Session
                </button>

              </div>
            </div>
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Filter Applications</h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="submitted">Submitted</option>
                    <option value="under_review">Under Review</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="waitlist">Waitlist</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <select
                    value={filters.location_preference}
                    onChange={(e) => handleFilterChange('location_preference', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">All Locations</option>
                    <option value="Shibuya & Harajuku">Shibuya & Harajuku</option>
                    <option value="Ginza & Marunouchi">Ginza & Marunouchi</option>
                    <option value="Omotesando & Aoyama">Omotesando & Aoyama</option>
                    <option value="Shinjuku">Shinjuku</option>
                    <option value="Roppongi">Roppongi</option>
                    <option value="Setagaya City">Setagaya City</option>
                    <option value="Nakameguro">Nakameguro</option>
                      <option value="Koto City">Koto City</option>
                    <option value="Flexible">Flexible</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Career Stage</label>
                  <select
                    value={filters.career_stage}
                    onChange={(e) => handleFilterChange('career_stage', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">All Stages</option>
                    <option value="Student">Student</option>
                    <option value="Part-Time">Part-Time</option>
                    <option value="Early Career">Early Career</option>
                    <option value="Mid-Career">Mid-Career</option>
                    <option value="Senior Level">Senior Level</option>
                    <option value="Executive">Executive</option>
                    <option value="Entrepreneur">Entrepreneur</option>
                    <option value="Retired">Retired</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="text"
                    value={filters.email}
                    onChange={(e) => handleFilterChange('email', e.target.value)}
                    placeholder="Search by email..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div className="flex items-end space-x-2">
                  <button
                    onClick={applyFilters}
                    className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 flex items-center"
                  >
                    <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
                    Filter
                  </button>
                  <button
                    onClick={clearFilters}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedItems.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-800 font-medium">
                      {selectedItems.length} applications selected
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={createManualSession}
                      className="bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700 flex items-center text-sm"
                      disabled={selectedItems.length < 2 || selectedItems.length > 4}
                    >
                      <PlusIcon className="w-4 h-4 mr-1" />
                      Create Session ({selectedItems.length}/4)
                    </button>
                    <button
                      onClick={() => setSelectedItems([])}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 text-sm"
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Applications List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Curated Fika Applications ({pagination.total})
                </h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItems(applications.map(app => app.id));
                            } else {
                              setSelectedItems([]);
                            }
                          }}
                          className="rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Applicant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Background
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Interests
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subscription
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expires
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Applied
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {applications.map(app => {
                      // ✅ Calculate subscription info for this applicant
                      const subscriptionInfo = getSubscriptionDisplay(app);
                      const expiryInfo = formatSubscriptionExpiry(app.subscription_expires);
                      
                      return (
                        <tr key={app.id} className={selectedItems.includes(app.id) ? 'bg-amber-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedItems.includes(app.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedItems(prev => [...prev, app.id]);
                                } else {
                                  setSelectedItems(prev => prev.filter(id => id !== app.id));
                                }
                              }}
                              className="rounded"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {app.first_name} {app.last_name}
                              </div>
                              <div className="text-sm text-gray-500">{app.email}</div>
                              <div className="text-xs text-gray-400">{app.nationality}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              <div className="font-medium">{app.profession}</div>
                              <div className="text-gray-600 text-xs">{app.industry}</div>
                              <div className="text-gray-500 text-xs">{app.career_stage}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs">
                              {app.interests && app.interests.slice(0, 2).map((interest, idx) => (
                                <span key={idx} className="inline-block bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs mr-1 mb-1">
                                  {interest}
                                </span>
                              ))}
                              {app.interests && app.interests.length > 2 && (
                                <span className="text-xs text-gray-500">+{app.interests.length - 2} more</span>
                              )}
                            </div>
                          </td>
                          
                          {/* ✅ SUBSCRIPTION COLUMN */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${subscriptionInfo.color}`}>
                              {subscriptionInfo.icon} {subscriptionInfo.label}
                            </span>
                          </td>
                          
                          {/* ✅ EXPIRY COLUMN */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {expiryInfo ? (
                              <div className={`text-sm ${expiryInfo.color}`}>
                                {expiryInfo.text}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400">—</div>
                            )}
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(app.application_status)}`}>
                              {app.application_status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(app.submitted_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex space-x-1">
                              {app.application_status === 'submitted' && (
                                <>
                                  <button
                                    onClick={() => updateApplicationStatus(app.id, 'approved')}
                                    className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs hover:bg-green-200"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={() => updateApplicationStatus(app.id, 'rejected')}
                                    className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs hover:bg-red-200"
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => {
                                  const subscriptionDetails = app.subscription_plan_name 
                                    ? `\n\nSubscription: ${subscriptionInfo.label}\nExpires: ${expiryInfo?.text || 'N/A'}`
                                    : '\n\nSubscription: Not Subscribed';
                                    
                                  const details = `Application Details:\n\nName: ${app.first_name} ${app.last_name}\nEmail: ${app.email}\nProfession: ${app.profession}\nIndustry: ${app.industry}\nInterests: ${app.interests?.join(', ') || 'None'}\nGoals: ${app.fika_goals?.join(', ') || 'None'}\nLocation: ${app.location_preference}\nSchedule: ${app.schedule_preference || 'Not specified'}${subscriptionDetails}\nAdditional: ${app.additional_info || 'None'}`;
                                  alert(details);
                                }}
                                className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs hover:bg-blue-200"
                              >
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                </table>
              </div>

              {/* Pagination */}
              {/* Pagination - UPDATED VERSION */}
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={async () => {
                      const newOffset = Math.max(0, pagination.offset - pagination.limit);
                      const newPagination = { ...pagination, offset: newOffset };
                      await loadApplicationsWithPagination(newPagination);
                    }}
                    disabled={pagination.offset === 0}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={async () => {
                      const newOffset = pagination.offset + pagination.limit;
                      const newPagination = { ...pagination, offset: newOffset };
                      await loadApplicationsWithPagination(newPagination);
                    }}
                    disabled={pagination.offset + pagination.limit >= pagination.total}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">{pagination.offset + 1}</span>
                      {' '}to{' '}
                      <span className="font-medium">
                        {Math.min(pagination.offset + pagination.limit, pagination.total)}
                      </span>
                      {' '}of{' '}
                      <span className="font-medium">{pagination.total}</span>
                      {' '}results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={async () => {
                          const newOffset = Math.max(0, pagination.offset - pagination.limit);
                          const newPagination = { ...pagination, offset: newOffset };
                          await loadApplicationsWithPagination(newPagination);
                        }}
                        disabled={pagination.offset === 0}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Previous</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      
                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, Math.ceil(pagination.total / pagination.limit)) }, (_, i) => {
                        const currentPage = Math.floor(pagination.offset / pagination.limit);
                        const pageNumber = i + Math.max(0, currentPage - 2);
                        const isCurrentPage = pageNumber === currentPage;
                        
                        if (pageNumber >= Math.ceil(pagination.total / pagination.limit)) return null;
                        
                        return (
                          <button
                            key={pageNumber}
                            onClick={async () => {
                              const newOffset = pageNumber * pagination.limit;
                              const newPagination = { ...pagination, offset: newOffset };
                              await loadApplicationsWithPagination(newPagination);
                            }}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              isCurrentPage
                                ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNumber + 1}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={async () => {
                          const newOffset = pagination.offset + pagination.limit;
                          const newPagination = { ...pagination, offset: newOffset };
                          await loadApplicationsWithPagination(newPagination);
                        }}
                        disabled={pagination.offset + pagination.limit >= pagination.total}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Next</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>

             


            </div>
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="space-y-6">
            {/* Session Filters */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Filter Sessions</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={filters.session_status || ''}
                    onChange={(e) => handleFilterChange('session_status', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

          

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Venue Area</label>
                  <select
                    value={filters.venue_area || ''}
                    onChange={(e) => handleFilterChange('venue_area', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">All Areas</option>
                    <option value="Shibuya & Harajuku">Shibuya & Harajuku</option>
                    <option value="Ginza & Marunouchi">Ginza & Marunouchi</option>
                    <option value="Omotesando & Aoyama">Omotesando & Aoyama</option>
                    <option value="Shinjuku">Shinjuku</option>
                    <option value="Roppongi">Roppongi</option>
                    <option value="Setagaya City">Setagaya City</option>
                    <option value="Nakameguro">Nakameguro</option>
                    <option value="Kitazawa">Kitazawa</option>
                    <option value="Minato">Minato</option>
                    <option value="Koto City">Koto City</option>
                  </select>
                </div>

                {/*}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                  <input
                    type="date"
                    value={filters.date_from || ''}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                */}

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
                    <input
                      type="date"
                      value={filters.date_from || ''}
                      onChange={(e) => handleFilterChange('date_from', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
                    <input
                      type="date"
                      value={filters.date_to || ''}
                      onChange={(e) => handleFilterChange('date_to', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>

                <div className="flex items-end space-x-2">
                  <button
                    onClick={applySessionFilters}
                    className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 flex items-center"
                  >
                    <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
                    Filter
                  </button>
                  <button
                    onClick={clearSessionFilters}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>

            {/* Sessions List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Curated Fika Sessions ({sessions.length})
                </h3>
              </div>
              
              {sessions.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <SparklesIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p>No sessions found. Run AI matching to create sessions.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {sessions.map(session => (
                    <div key={session.id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="text-lg font-medium text-gray-900">
                              {session.session_title}
                            </h4>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSessionStatusColor(session.session_status)}`}>
                              {session.session_status}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                            <div>
                              <span className="font-medium">Date:</span> {formatDate(session.session_date)}
                            </div>
                            <div>
                              <span className="font-medium">Venue:</span> {session.venue_name}
                            </div>
                            <div>
                              <span className="font-medium">Area:</span> {session.venue_area}
                            </div>
                            <div>
                              <span className="font-medium">Participants:</span> {session.current_participants}/{session.max_participants}
                            </div>
                          </div>

                          {/* Participants Preview */}
                          <div className="mt-3 flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <UserGroupIcon className="w-4 h-4 text-gray-400" />
                              <div className="flex -space-x-1">
                                {(session.curated_fika_participants || []).slice(0, 3).map((participant, idx) => (
                                  <div
                                    key={idx}
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-600 text-xs font-medium border-2 border-white"
                                    title={`${participant.curated_fika_applications?.first_name} ${participant.curated_fika_applications?.last_name}`}
                                  >
                                    {participant.curated_fika_applications?.first_name?.[0]}{participant.curated_fika_applications?.last_name?.[0]}
                                  </div>
                                ))}
                                {(session.curated_fika_participants || []).length > 3 && (
                                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-600 text-xs font-medium border-2 border-white">
                                    +{session.curated_fika_participants.length - 3}
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="text-sm">
                              <span className="font-medium">Revenue:</span> ${session.total_revenue || (session.price_per_person * session.current_participants).toFixed(2)}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          {/* 
                          <button
                            onClick={() => {
                            
                            
                              setSelectedSession(session);
                              setSessionDetailModal(true);
                            }}
                            className="bg-blue-100 text-blue-700 px-3 py-2 rounded-md hover:bg-blue-200 flex items-center text-sm"
                          >
                            <EyeIcon className="w-4 h-4 mr-1" />
                            Details
                          </button>
                          */}

                          <button
                              onClick={async () => {
                                setLoading(true);
                                try {
                                  // Fetch complete session details with full participant data
                                  const result = await curatedFikaAdminService.getSessionDetails(session.id);
                                  if (result.success) {
                                    setSelectedSession(result.data);
                                    setSessionDetailModal(true);
                                  } else {
                                    alert('Error loading session details: ' + result.error);
                                  }
                                } catch (error) {
                                  alert('Error: ' + error.message);
                                }
                                setLoading(false);
                              }}
                              className="bg-blue-100 text-blue-700 px-3 py-2 rounded-md hover:bg-blue-200 flex items-center text-sm"
                            >
                              <EyeIcon className="w-4 h-4 mr-1" />
                              Details
                           </button>

                           <button
                            onClick={() => {
                              // ✅ Convert UTC to local datetime string for editing
                              const timezone = session.venue_timezone || 'Asia/Tokyo';
                              const localDateTimeString = session.session_date 
                                ? convertUTCToLocal(session.session_date, timezone)
                                : '';
                              
                              setEditingSession({
                                ...session,
                                session_date_local: localDateTimeString,  // Store local string for editing
                                manual_entry: true
                              });
                              setEditSessionModal(true);
                            }}
                            className="bg-gray-100 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-200 flex items-center text-sm"
                          >
                            <PencilIcon className="w-4 h-4 mr-1" />
                            Edit
                          </button>

                          {/* 11-6-25
                          <button
                            onClick={() => {
                              setEditingSession({
                                ...session,
                                manual_entry: true  // Default to manual entry
                              });
                              setEditSessionModal(true);
                            }}
                            className="bg-gray-100 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-200 flex items-center text-sm"
                          >
                            <PencilIcon className="w-4 h-4 mr-1" />
                            Edit
                          </button>
                          */}

                           {/* ADD DELETE BUTTON HERE - AFTER the select dropdown */}
                            <button
                              onClick={() => handleDeleteSession(session)}
                              className="bg-red-100 text-red-700 px-3 py-2 rounded-md hover:bg-red-200 flex items-center text-sm"
                              disabled={loading}
                            >
                              <TrashIcon className="w-4 h-4 mr-1" />
                              Delete
                            </button>

                          {session.session_status === 'scheduled' && (
                            <button
                              onClick={() => sendSessionInvitations(session.id, session.session_title)}
                              className="bg-green-100 text-green-700 px-3 py-2 rounded-md hover:bg-green-200 flex items-center text-sm"
                            >
                              <EnvelopeIcon className="w-4 h-4 mr-1" />
                              Send Invites
                            </button>
                          )}

                           {/* 9-23-25 NEW: Session-specific reminder button */}
                          {(session.session_status === 'confirmed' || session.session_status === 'scheduled') && (
                            <button
                              onClick={() => handleSendSessionReminder(session.id, session.session_title)}
                              className="bg-blue-100 text-blue-700 px-3 py-2 rounded-md hover:bg-blue-200 flex items-center text-sm"
                            >
                              <ClockIcon className="w-4 h-4 mr-1" />
                              Send Reminders
                            </button>
                          )}

                           {/* 9-25-25 NEW: Session-specific reminder button */}
                          {session.session_status === 'scheduled' && (
                            <button
                              onClick={() => handleSendConfirmationNudges(session.id, session.session_title)}
                              className="bg-orange-100 text-orange-700 px-3 py-2 rounded-md hover:bg-orange-200 flex items-center text-sm"
                            >
                              <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                              Nudge Non-Responders
                            </button>
                          )}


                          {session.session_status === 'completed' && (
                            <button
                            
                              onClick={(e) => {
                                e.stopPropagation();  // ✅ Prevent event bubbling
                                handleSendFeedbackEmails(session.id, session.session_title);  // ✅ Pass both ID and title
                              }}
                              className="bg-amber-100 text-amber-700 px-3 py-2 rounded-md hover:bg-amber-200 flex items-center text-sm"
                            >
                              <EnvelopeIcon className="w-4 h-4 mr-1" />
                              Send Feedback Emails
                            </button>
                          )}

                          <div className="relative">
                            <select
                              value={session.session_status}
                              onChange={(e) => updateSessionStatus(session.id, e.target.value)}
                              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-purple-500 focus:border-purple-500"
                            >
                              <option value="scheduled">Scheduled</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 10-15-25 Open Sessions */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Curated Fika Sessions ({sessions.length})
              </h3>
              <button
                onClick={() => setShowOpenSessionModal(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center text-sm"
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                Create Open Session
              </button>
            </div>

            {/* Session Detail Modal */}
            {sessionDetailModal && selectedSession && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">Session Details</h3>
                    <button
                      onClick={() => setSessionDetailModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XCircleIcon className="w-6 h-6" />
                    </button>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    {/* Session Information */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Session Information</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Title:</span> {selectedSession.session_title}
                        </div>
                        <div>
                          <span className="font-medium">Status:</span> 
                          <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs ${getSessionStatusColor(selectedSession.session_status)}`}>
                            {selectedSession.session_status}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Date:</span> {formatDateTime(selectedSession.session_date)}
                        </div>
                        <div>
                          <span className="font-medium">Duration:</span> {selectedSession.session_duration || 60} minutes
                        </div>
                        <div>
                          <span className="font-medium">Venue:</span> {selectedSession.venue_name}
                        </div>
                        <div>
                          <span className="font-medium">Address:</span> {selectedSession.venue_address}
                        </div>
                        <div>
                          <span className="font-medium">Area:</span> {selectedSession.venue_area}
                        </div>
                        <div>
                          <span className="font-medium">Price per Person:</span> ${selectedSession.price_per_person}
                        </div>
                      </div>
                      
                      {selectedSession.host_notes && (
                        <div className="mt-3">
                          <span className="font-medium">Host Notes:</span>
                          <p className="text-gray-600 mt-1">{selectedSession.host_notes}</p>
                        </div>
                      )}
                    </div>

                    {/* Participants */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Participants ({selectedSession.curated_fika_participants?.length || 0})</h4>
                      <div className="space-y-3">
                        {(selectedSession.curated_fika_participants || []).map((participant, idx) => {
                          const member = participant.curated_fika_applications;
                          if (!member) return null;
                          
                          return (
                            <div key={idx} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <h5 className="font-medium text-gray-900">
                                    {member.first_name} {member.last_name}
                                  </h5>
                                  <p className="text-sm text-gray-600">{member.email}</p>
                                </div>
                                <div className="flex space-x-2">
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getParticipantStatusColor(participant.participation_status)}`}>
                                    {participant.participation_status}
                                  </span>
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${participant.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                    {participant.payment_status}
                                  </span>

                                  {/* 9/22/25 */}
                                  {/* NEW: Delete Participant Button */}
                                  {(participant.participation_status === 'invited' || 
                                    participant.participation_status === 'confirmed') && (
                                    <button
                                      onClick={() => handleDeleteParticipant(participant, selectedSession)}
                                      className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs hover:bg-red-200 flex items-center"
                                      disabled={loading}
                                    >
                                      <TrashIcon className="w-3 h-3 mr-1" />
                                      Remove
                                    </button>
                                  )}

                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                                <div>
                                  <span className="font-medium">Profession:</span> {member.profession}
                                </div>
                                <div>
                                  <span className="font-medium">Industry:</span> {member.industry}
                                </div>

                                <div>
                                  <span className="font-medium">Career Stage:</span> {member.career_stage}
                                </div>
                                <div>
                                  <span className="font-medium">City:</span> {member.city}, {member.country}
                                </div>


                                <div className="col-span-2">
                                  <span className="font-medium">Interests:</span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {(member.interests || []).map((interest, i) => (
                                      <span key={i} className="inline-block bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs">
                                        {interest}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* ADD FIKA GOALS: */}
                              {member.fika_goals && member.fika_goals.length > 0 && (
                                <div className="col-span-2 mt-2">
                                  <span className="font-medium text-sm text-gray-600">Fika Goals:</span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {member.fika_goals.map((goal, i) => (
                                      <span key={i} className="inline-block bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs">
                                        {goal}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                               {/* NEW: PREFERENCES SECTION */}
                                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                                  <div>
                                    <span className="font-medium">Location Preference:</span>
                                    {member.location_preference ? (
                                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                        📍 {member.location_preference}
                                      </span>
                                    ) : (
                                      <span className="ml-2 text-gray-400">Not specified</span>
                                    )}
                                  </div>
                                  <div>
                                    <span className="font-medium">Schedule Preference:</span>
                                    {member.schedule_preference ? (
                                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                        ⏰ {member.schedule_preference}
                                      </span>
                                    ) : (
                                      <span className="ml-2 text-gray-400">Not specified</span>
                                    )}
                                  </div>
                                </div>
                              
                              {participant.payment_amount && (
                                <div className="mt-3 text-sm">
                                  <span className="font-medium">Payment Amount:</span> ${participant.payment_amount}
                                  {participant.payment_date && (
                                    <span className="text-gray-500 ml-2">
                                      (Paid: {formatDate(participant.payment_date)})
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* 9/22/25 */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        {!showAddParticipant ? (
                          <button
                            onClick={() => {
                              setShowAddParticipant(true);
                              loadAvailableApplications();
                            }}
                            className="bg-green-100 text-green-700 px-3 py-2 rounded-md hover:bg-green-200 flex items-center text-sm"
                            disabled={selectedSession.current_participants >= selectedSession.max_participants}
                          >
                            <PlusIcon className="w-4 h-4 mr-1" />
                            Add Participant ({selectedSession.current_participants}/{selectedSession.max_participants})
                          </button>
                        ) : (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h5 className="font-medium text-green-800 mb-3">Add New Participant</h5>
                            
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Select Application
                                </label>
                                <select
                                  value={selectedApplicationId}
                                  onChange={(e) => setSelectedApplicationId(e.target.value)}
                                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-green-500 focus:border-green-500"
                                >
                                  <option value="">-- Select an approved application --</option>
                                  {availableApplications.map(app => (
                                    <option key={app.id} value={app.id}>
                                      {app.first_name} {app.last_name} ({app.email}) - {app.profession}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              
                              <div className="flex space-x-2">
                                <button
                                  onClick={handleAddParticipant}
                                  disabled={!selectedApplicationId || loading}
                                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                                >
                                  Add & Send Invitation
                                </button>
                                <button
                                  onClick={() => {
                                    setShowAddParticipant(false);
                                    setSelectedApplicationId('');
                                  }}
                                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                            
                            {availableApplications.length === 0 && (
                              <p className="text-sm text-gray-600 mt-2">
                                No available approved premium applications to add.
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Session Modal - UPDATED WITH VENUE SELECTOR */}
            {editSessionModal && editingSession && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">Edit Session</h3>
                    <button
                      onClick={() => setEditSessionModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XCircleIcon className="w-6 h-6" />
                    </button>
                  </div>
                  
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Session Title
                        </label>
                        <input
                          type="text"
                          value={editingSession.session_title}
                          onChange={(e) => setEditingSession({
                            ...editingSession,
                            session_title: e.target.value
                          })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Session Date & Time
                          <span className="text-sm font-medium text-blue-600 ml-2">
                            ({editingSession?.city || 'Tokyo'} local time)
                          </span>
                        </label>
                        <input
                          type="datetime-local"
                          value={editingSession.session_date_local || ''}
                          onChange={(e) => setEditingSession({
                            ...editingSession,
                            session_date_local: e.target.value
                          })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Pick the date and time as it should appear in {editingSession?.city || 'Tokyo'}
                        </p>
                      </div>

                      {/* 11-6-25
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Session Date & Time
                          <span className="text-sm font-medium text-blue-600 ml-2">
                            ({editingSession?.city || 'Tokyo'} local time)
                          </span>
                        </label>
                        <input
                          type="datetime-local"
                          value={editingSession.session_date ? convertUTCToLocal(editingSession.session_date, editingSession.venue_timezone || 'Asia/Tokyo') : ''}
                          onChange={(e) => setEditingSession({
                            ...editingSession,
                            session_date: e.target.value
                          })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter the time as participants should see it in {editingSession?.city || 'Tokyo'}
                        </p>
                      </div>
                      */}

                    </div>

                    {/* VENUE SELECTION SECTION - NEW */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-700">Venue Selection</h4>
                        <button
                          onClick={() => setEditingSession({
                            ...editingSession,
                            manual_entry: !editingSession.manual_entry
                          })}
                          className="text-sm text-purple-600 hover:text-purple-800"
                        >
                          {editingSession.manual_entry ? 'Use Venue Selector' : 'Manual Entry'}
                        </button>
                      </div>

                      {editingSession.manual_entry ? (
                        // Manual entry (existing code)
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Venue Name
                              </label>
                              <input
                                type="text"
                                value={editingSession.venue_name}
                                onChange={(e) => setEditingSession({
                                  ...editingSession,
                                  venue_name: e.target.value
                                })}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Venue Area
                              </label>
                              <select
                                value={editingSession.venue_area}
                                onChange={(e) => setEditingSession({
                                  ...editingSession,
                                  venue_area: e.target.value
                                })}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                              >
                                <option value="Shibuya & Harajuku">Shibuya & Harajuku</option>
                                <option value="Ginza & Marunouchi">Ginza & Marunouchi</option>
                                <option value="Omotesando & Aoyama">Omotesando & Aoyama</option>
                                <option value="Shinjuku">Shinjuku</option>
                                <option value="Roppongi">Roppongi</option>
                                <option value="Nakameguro">Nakameguro</option>
                                <option value="Setagaya City">Setagaya City</option>
                                <option value="Kitazawa">Kitazawa</option>
                                <option value="Minato">Minato</option>
                                  <option value="Koto City">Koto City</option>
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Venue Address
                            </label>
                            <input
                              type="text"
                              value={editingSession.venue_address}
                              onChange={(e) => setEditingSession({
                                ...editingSession,
                                venue_address: e.target.value
                              })}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                          </div>
                        </>
                      ) : (
                        // NEW: Venue Selector
                        <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                          <VenueSelector
                            selectedVenue={editingSession}
                            city={editingSession.city || 'Tokyo'}
                            onVenueSelect={(venueData) => setEditingSession({
                              ...editingSession,
                              ...venueData
                            })}
                          />
                        </div>
                      )}
                    </div>
                    {/* END VENUE SELECTION SECTION */}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Price per Person ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={editingSession.price_per_person}
                          onChange={(e) => setEditingSession({
                            ...editingSession,
                            price_per_person: parseFloat(e.target.value)
                          })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Duration (minutes)
                        </label>
                        <input
                          type="number"
                          value={editingSession.session_duration || 90}
                          onChange={(e) => setEditingSession({
                            ...editingSession,
                            session_duration: parseInt(e.target.value)
                          })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Host Notes
                      </label>
                      <textarea
                        value={editingSession.host_notes || ''}
                        onChange={(e) => setEditingSession({
                          ...editingSession,
                          host_notes: e.target.value
                        })}
                        rows={3}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Any special instructions or notes for this session..."
                      />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        onClick={() => setEditSessionModal(false)}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => updateSessionDetails(editingSession.id, editingSession)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Open Session Creation Modal 10-15-25 */}
            {showOpenSessionModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-3xl w-full max-h-screen overflow-y-auto">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">
                      Create Open Registration Session
                    </h3>
                    <button
                      onClick={() => setShowOpenSessionModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XCircleIcon className="w-6 h-6" />
                    </button>
                  </div>
                  
                  <div className="p-6 space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-blue-800">
                        <strong>Open Registration:</strong> Members can self-register for this session 
                        from their member portal. No participants are pre-selected.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Session Title *
                        </label>
                        <input
                          type="text"
                          value={openSessionData.session_title}
                          onChange={(e) => setOpenSessionData({
                            ...openSessionData, 
                            session_title: e.target.value
                          })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="e.g., Tech Innovators Coffee"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Session Date & Time *
                        </label>
                        <input
                          type="datetime-local"
                          value={openSessionData.session_date}
                          onChange={(e) => setOpenSessionData({
                            ...openSessionData, 
                            session_date: e.target.value
                          })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          required
                        />
                      </div>
                    </div>

                    {/* Venue Selection - Use existing VenueSelector component */}
                    <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                      <VenueSelector
                        selectedVenue={openSessionData}
                        city={openSessionData.city}
                        onVenueSelect={(venueData) => setOpenSessionData({
                          ...openSessionData,
                          venue_name: venueData.venue_name,
                          venue_address: venueData.venue_address,
                          venue_area: venueData.venue_area,
                          venue_id: venueData.venue_id
                        })}
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Participants *
                        </label>
                        <input
                          type="number"
                          value={openSessionData.max_participants}
                          onChange={(e) => setOpenSessionData({
                            ...openSessionData, 
                            max_participants: parseInt(e.target.value)
                          })}
                          min="2"
                          max="4"
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Duration (min)
                        </label>
                        <input
                          type="number"
                          value={openSessionData.session_duration}
                          onChange={(e) => setOpenSessionData({
                            ...openSessionData, 
                            session_duration: parseInt(e.target.value)
                          })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Price ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={openSessionData.price_per_person}
                          onChange={(e) => setOpenSessionData({
                            ...openSessionData, 
                            price_per_person: parseFloat(e.target.value)
                          })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Host Notes (Optional)
                      </label>
                      <textarea
                        value={openSessionData.host_notes}
                        onChange={(e) => setOpenSessionData({
                          ...openSessionData, 
                          host_notes: e.target.value
                        })}
                        rows={3}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder="Any special instructions or themes for this session..."
                      />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <button
                        onClick={() => setShowOpenSessionModal(false)}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateOpenSession}
                        disabled={loading}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        {loading ? 'Creating...' : 'Create Open Session'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}


          </div>

          


        )}

        {/* Matching Tab */}
        {activeTab === 'matching' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <BoltIcon className="w-6 h-6 text-purple-600 mr-3" />
                AI Matching Algorithm
              </h3>

              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-blue-900 mb-3">Geographic Scope</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">
                      City *
                    </label>
                    <select
                      value={matchingFilters.city || 'Tokyo'}
                      onChange={(e) => setMatchingFilters(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full border border-blue-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="Tokyo">Tokyo</option>
                      <option value="Osaka">Osaka</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">
                      Country *
                    </label>
                    <select
                      value={matchingFilters.country || 'Japan'}
                      onChange={(e) => setMatchingFilters(prev => ({ ...prev, country: e.target.value }))}
                      className="w-full border border-blue-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="Japan">Japan</option>
                    </select>
                  </div>
                </div>
                <p className="text-sm text-blue-700 mt-2">
                  Matching will only include applications from the selected city and country
                </p>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-purple-900 mb-2">How AI Matching Works for Curated Fika:</h4>
                <ul className="text-sm text-purple-800 space-y-1">
                  <li>• Analyzes professional backgrounds and career stages for balanced conversations</li>
                  <li>• Matches interests and fika goals for authentic connection potential</li>
                  <li>• Considers communication styles and personality compatibility</li>
                  <li>• Optimizes for location preferences and schedule availability</li>
                  <li>• Creates intimate groups of 2-4 people for meaningful dialogue</li>
                  <li>• Prioritizes premium member applications with high compatibility scores</li>
                </ul>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={runAIMatching}
                  disabled={loading}
                  className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center text-lg font-medium"
                >
                  <BoltIcon className="w-6 h-6 mr-3" />
                  {loading ? 'Running AI Matching...' : `Run AI Matching for ${matchingFilters.city || 'Tokyo'}, ${matchingFilters.country || 'Japan'}`}
                </button>
              </div>

              {stats && (
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{stats.applications?.approved || 0}</div>
                    <div className="text-sm text-gray-600">Approved Applications</div>
                    <div className="text-xs text-gray-500 mt-1">Ready for matching</div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{stats.sessions?.total || 0}</div>
                    <div className="text-sm text-gray-600">Total Sessions</div>
                    <div className="text-xs text-gray-500 mt-1">Successfully created</div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {stats.applications?.approved >= 2 ? Math.floor((stats.applications?.approved || 0) / 3) : 0}
                    </div>
                    <div className="text-sm text-gray-600">Potential Sessions</div>
                    <div className="text-xs text-gray-500 mt-1">From approved apps</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 9-17-25 */}
        {/* Feedback Tab - ADD THIS ENTIRE SECTION */}
        {activeTab === 'feedback' && (
          <div className="space-y-6">
            {/* Feedback Stats */}
            {feedbackStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <HeartIcon className="w-8 h-8 text-pink-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Responses</p>
                      <p className="text-2xl font-bold text-gray-900">{feedbackStats.total_responses || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <StarIcon className="w-8 h-8 text-yellow-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Avg Satisfaction</p>
                      <p className="text-2xl font-bold text-gray-900">{feedbackStats.average_ratings?.overall_satisfaction || 0}/10</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <UserGroupIcon className="w-8 h-8 text-blue-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Connections Made</p>
                      <p className="text-2xl font-bold text-gray-900">{feedbackStats.connection_stats?.connections_made_percent || 0}%</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <SparklesIcon className="w-8 h-8 text-purple-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Would Attend Again</p>
                      <p className="text-2xl font-bold text-gray-900">{feedbackStats.connection_stats?.would_attend_again_percent || 0}%</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Feedback Filters */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Filter Feedback</h3>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Satisfaction</label>
                  <select
                    value={feedbackFilters.min_satisfaction}
                    onChange={(e) => handleFeedbackFilterChange('min_satisfaction', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">All Ratings</option>
                    <option value="8">8+ (Excellent)</option>
                    <option value="6">6+ (Good)</option>
                    <option value="4">4+ (Fair)</option>
                    <option value="1">1+ (All)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Venue Area</label>
                  <select
                    value={feedbackFilters.venue_area}
                    onChange={(e) => handleFeedbackFilterChange('venue_area', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">All Areas</option>
                    <option value="Shibuya & Harajuku">Shibuya & Harajuku</option>
                    <option value="Ginza & Marunouchi">Ginza & Marunouchi</option>
                    <option value="Omotesando & Aoyama">Omotesando & Aoyama</option>
                    <option value="Shinjuku">Shinjuku</option>
                    <option value="Roppongi">Roppongi</option>
                    <option value="Setagaya City">Setagaya City</option>
                    <option value="Nakameguro">Nakameguro</option>
                    <option value="Kitazawa">Kitazawa</option>
                    <option value="Minato">Minato</option>
                      <option value="Koto City">Koto City</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Connections Made</label>
                  <select
                    value={feedbackFilters.connection_made}
                    onChange={(e) => handleFeedbackFilterChange('connection_made', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">All</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Would Attend Again</label>
                  <select
                    value={feedbackFilters.would_attend_again}
                    onChange={(e) => handleFeedbackFilterChange('would_attend_again', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">All</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
                  <input
                    type="date"
                    value={feedbackFilters.date_from}
                    onChange={(e) => handleFeedbackFilterChange('date_from', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div className="flex items-end space-x-2">
                  <button
                    onClick={applyFeedbackFilters}
                    className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 flex items-center"
                  >
                    <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
                    Filter
                  </button>
                  <button
                    onClick={clearFeedbackFilters}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>

            {/* Feedback List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Session Feedback ({feedbackPagination.total})
                </h3>
              </div>
              
              {feedback.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <HeartIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p>No feedback responses found.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {feedback.map(feedbackItem => (
                    <div key={feedbackItem.id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="text-lg font-medium text-gray-900">
                              {feedbackItem.curated_fika_sessions?.session_title}
                            </h4>
                            <span className="text-sm text-gray-500">
                              {formatDate(feedbackItem.curated_fika_sessions?.session_date)}
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                            <span>{feedbackItem.curated_fika_sessions?.venue_name}</span>
                            <span>{feedbackItem.curated_fika_sessions?.venue_area}</span>
                            <span>
                              By: {feedbackItem.curated_fika_participants?.curated_fika_applications?.first_name} {feedbackItem.curated_fika_participants?.curated_fika_applications?.last_name}
                            </span>
                          </div>

                          {/* Rating Summary */}
                          <div className="grid grid-cols-5 gap-4 mb-4">
                            <div className="text-center">
                              <div className={`text-lg font-bold ${getRatingColor(feedbackItem.overall_satisfaction)}`}>
                                {feedbackItem.overall_satisfaction}/10
                              </div>
                              <div className="text-xs text-gray-500">Overall</div>
                            </div>
                            <div className="text-center">
                              <div className={`text-lg font-bold ${getRatingColor(feedbackItem.venue_rating)}`}>
                                {feedbackItem.venue_rating}/10
                              </div>
                              <div className="text-xs text-gray-500">Venue</div>
                            </div>
                            <div className="text-center">
                              <div className={`text-lg font-bold ${getRatingColor(feedbackItem.group_chemistry)}`}>
                                {feedbackItem.group_chemistry}/10
                              </div>
                              <div className="text-xs text-gray-500">Chemistry</div>
                            </div>
                            <div className="text-center">
                              <div className={`text-lg font-bold ${getRatingColor(feedbackItem.conversation_quality)}`}>
                                {feedbackItem.conversation_quality}/10
                              </div>
                              <div className="text-xs text-gray-500">Conversation</div>
                            </div>
                            <div className="text-center">
                              <div className={`text-lg font-bold ${getRatingColor(feedbackItem.likelihood_to_recommend)}`}>
                                {feedbackItem.likelihood_to_recommend}/10
                              </div>
                              <div className="text-xs text-gray-500">Recommend</div>
                            </div>
                          </div>

                          {/* Connection Info */}
                          <div className="flex items-center space-x-6 text-sm mb-3">
                            <div className="flex items-center">
                              <span className="font-medium">Connections Made:</span>
                              <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                                feedbackItem.connection_made 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {feedbackItem.connection_made ? 'Yes' : 'No'}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <span className="font-medium">Would Attend Again:</span>
                              <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                                feedbackItem.would_attend_again 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {feedbackItem.would_attend_again ? 'Yes' : 'No'}
                              </span>
                            </div>
                            {feedbackItem.individual_connections > 0 && (
                              <div>
                                <span className="font-medium">Individual Connections:</span>
                                <span className="ml-1 text-blue-600">{feedbackItem.individual_connections}</span>
                              </div>
                            )}
                             {/* NEW SECTION */}
                            {feedbackItem.preferred_group_size && (
                              <div>
                                <span className="font-medium">Preferred Group Size:</span>
                                <span className="ml-1 px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                                  {feedbackItem.preferred_group_size} people
                                </span>
                              </div>
                            )}

                          </div>

                            {/* ✅ NEW SECTION: Session Attendance Information */}
                            {(feedbackItem.all_attendees_present !== null || 
                              feedbackItem.expected_attendees !== null || 
                              feedbackItem.actual_attendees !== null || 
                              feedbackItem.attendee_notes) && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                                <h5 className="font-medium text-blue-900 text-sm mb-2">Session Attendance</h5>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  {feedbackItem.all_attendees_present !== null && (
                                    <div className="flex items-center">
                                      <span className="font-medium text-blue-800">All Attendees Present:</span>
                                      <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                                        feedbackItem.all_attendees_present 
                                          ? 'bg-green-100 text-green-800' 
                                          : 'bg-orange-100 text-orange-800'
                                      }`}>
                                        {feedbackItem.all_attendees_present ? '✓ Yes' : '✗ No'}
                                      </span>
                                    </div>
                                  )}
                                  
                                  {(feedbackItem.expected_attendees !== null || feedbackItem.actual_attendees !== null) && (
                                    <div className="flex items-center">
                                      <span className="font-medium text-blue-800">Attendance:</span>
                                      <span className="ml-2 text-blue-700">
                                        {feedbackItem.actual_attendees || 0} / {feedbackItem.expected_attendees || 0} attendees
                                      </span>
                                    </div>
                                  )}
                                </div>
                                
                                {feedbackItem.attendee_notes && (
                                  <div className="mt-2">
                                    <span className="font-medium text-blue-800 text-sm">Attendee Notes:</span>
                                    <p className="text-blue-700 text-sm mt-1 italic">
                                      "{feedbackItem.attendee_notes}"
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                            {/* ✅ END NEW SECTION */}

                          {/* Comments */}
                          {(feedbackItem.what_worked_well || feedbackItem.what_could_improve || feedbackItem.memorable_moments) && (
                            <div className="space-y-2 text-sm">
                              {feedbackItem.what_worked_well && (
                                <div>
                                  <span className="font-medium text-green-700">What worked well:</span>
                                  <p className="text-gray-600 mt-1">{feedbackItem.what_worked_well}</p>
                                </div>
                              )}
                              {feedbackItem.what_could_improve && (
                                <div>
                                  <span className="font-medium text-orange-700">Could improve:</span>
                                  <p className="text-gray-600 mt-1">{feedbackItem.what_could_improve}</p>
                                </div>
                              )}
                              {feedbackItem.memorable_moments && (
                                <div>
                                  <span className="font-medium text-purple-700">Memorable moments:</span>
                                  <p className="text-gray-600 mt-1">{feedbackItem.memorable_moments}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="text-sm text-gray-500 text-right">
                          <div>Submitted</div>
                          <div>{formatDate(feedbackItem.submitted_at)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {feedbackPagination.total > feedbackPagination.limit && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => {
                        const newOffset = Math.max(0, feedbackPagination.offset - feedbackPagination.limit);
                        setFeedbackPagination(prev => ({ ...prev, offset: newOffset }));
                        loadFeedback();
                      }}
                      disabled={feedbackPagination.offset === 0}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => {
                        const newOffset = feedbackPagination.offset + feedbackPagination.limit;
                        setFeedbackPagination(prev => ({ ...prev, offset: newOffset }));
                        loadFeedback();
                      }}
                      disabled={feedbackPagination.offset + feedbackPagination.limit >= feedbackPagination.total}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{' '}
                        <span className="font-medium">{feedbackPagination.offset + 1}</span>
                        {' '}to{' '}
                        <span className="font-medium">
                          {Math.min(feedbackPagination.offset + feedbackPagination.limit, feedbackPagination.total)}
                        </span>
                        {' '}of{' '}
                        <span className="font-medium">{feedbackPagination.total}</span>
                        {' '}results
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analytics Tab - Placeholder */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <ChartBarIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Advanced Analytics</h3>
              <p className="text-gray-600 mb-4">
                Deep insights and performance metrics - Phase 3 implementation
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800 text-sm">
                  Phase 3 will include: Revenue analytics, success rate tracking, member satisfaction scores, and predictive matching insights
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CuratedFikaAdmin;